import { CONFIG } from '../config/config.js';

export const PASSWORD_STRENGTH = {
  NONE: 'none',
  WEAK: 'weak',
  MEDIUM: 'medium',
  GOOD: 'good',
  STRONG: 'strong'
};

class SessionManager {
  constructor() {
    this.SESSION_TIMEOUT = CONFIG.SESSION_TIMEOUT_MINUTES * 60 * 1000; // Convert minutes to milliseconds
    this.WARNING_TIME = CONFIG.SESSION_WARNING_MINUTES * 60 * 1000; // Convert minutes to milliseconds
    this.ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    this.sessionTimer = null;
    this.warningTimer = null;
    this.lastActivity = Date.now();
    this.isWarningShown = false;
    this.warningCallback = null;
    this.timeoutCallback = null;
    this.isSessionActive = false; // Track if session management is active
    this.activityListenersBound = false; // Track if activity listeners are bound
    this.activityHandler = null; // Store activity handler reference for cleanup

    this.sessionData = null;

    // Don't bind activity listeners in constructor - wait for session to start
  }

  startSession(userInfo) {
    console.log('SessionManager: Starting session for user:', userInfo.email);
    this.sessionData = {
      userId: userInfo.userId,
      email: userInfo.email,
      username: userInfo.username,
      loginTime: new Date().toISOString(),
      lastActivity: new Date().toISOString(),
      sessionId: this.generateSessionId(),
      isExternal: userInfo.isExternal
    };

    this.isSessionActive = true;
    this.bindActivityListeners(); // Only bind when session starts
    this.resetSessionTimer();
    return this.sessionData;
  }

  getSession() {
    return this.sessionData;
  }

  updateActivity() {
    // Only process activity updates when session is active
    if (!this.isSessionActive || !this.sessionData) {
      console.log('SessionManager: Activity detected but session is not active. Ignoring.');
      return;
    }

    console.log('SessionManager: User activity detected, resetting session timer');
    this.lastActivity = Date.now();
    this.sessionData.lastActivity = new Date().toISOString();

    if (this.isWarningShown) {
      this.hideWarning();
    }

    this.resetSessionTimer();
  }

  generateSessionId() {
    return `session_${crypto.randomUUID()}`;
  }

  bindActivityListeners() {
    if (this.activityListenersBound) {
      return; // Avoid binding multiple times
    }

    this.activityHandler = () => this.updateActivity();
    for (const event of this.ACTIVITY_EVENTS) {
      document.addEventListener(event, this.activityHandler, true);
    }
    this.activityListenersBound = true;
  }

  unbindActivityListeners() {
    if (!this.activityListenersBound || !this.activityHandler) {
      return;
    }

    for (const event of this.ACTIVITY_EVENTS) {
      document.removeEventListener(event, this.activityHandler, true);
    }
    this.activityListenersBound = false;
    this.activityHandler = null;
  }

  resetSessionTimer() {
    this.clearTimers();

    this.warningTimer = setTimeout(() => {
      this.showWarning();
    }, this.SESSION_TIMEOUT - this.WARNING_TIME);

    this.sessionTimer = setTimeout(() => {
      this.handleSessionTimeout();
    }, this.SESSION_TIMEOUT);
  }

  clearTimers() {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  showWarning() {
    this.isWarningShown = true;
    if (this.warningCallback) {
      this.warningCallback();
    }
  }

  hideWarning() {
    this.isWarningShown = false;
  }

  handleSessionTimeout() {
    this.clearSession();
    if (this.timeoutCallback) {
      this.timeoutCallback();
    }
  }

  clearSession() {
    this.sessionData = null;
    this.clearTimers();
    this.isWarningShown = false;
    this.isSessionActive = false;
    this.unbindActivityListeners(); // Remove activity listeners when session ends
  }

  setWarningCallback(callback) {
    this.warningCallback = callback;
  }

  setTimeoutCallback(callback) {
    this.timeoutCallback = callback;
  }

  isSessionManagementActive() {
    return this.isSessionActive && this.sessionData !== null;
  }

  enableSessionManagement() {
    // If sessionData exists, ensure session is active and timers are reset
    // This is called from App.jsx after callbacks are set
    if (this.sessionData) {
      if (!this.isSessionActive) {
        this.isSessionActive = true;
        this.bindActivityListeners();
      }
      // Always reset timers to ensure callbacks are used
      this.resetSessionTimer();
    }
  }

  disableSessionManagement() {
    this.isSessionActive = false;
    this.unbindActivityListeners();
    this.clearTimers();
    this.isWarningShown = false;
  }

  isSessionValid() {
    if (!this.sessionData) {
      return false;
    }

    // Check if session has required fields
    if (!this.sessionData.email || !this.sessionData.role) {
      console.log('SessionManager: Session missing required fields (email or role)');
      return false;
    }

    // Check session timestamp
    const lastActivity = new Date(this.sessionData.lastActivity);
    const now = new Date();
    const timeDiff = now - lastActivity;

    if (timeDiff >= this.SESSION_TIMEOUT) {
      return false;
    }

    return true;
  }

  getRemainingTime() {
    if (!this.sessionData) {
      return 0;
    }

    const lastActivity = new Date(this.sessionData.lastActivity);
    const now = new Date();
    const elapsed = now - lastActivity;
    const remaining = this.SESSION_TIMEOUT - elapsed;

    return Math.max(0, Math.ceil(remaining / (60 * 1000)));
  }
}

// Utility functions for account lockout based on API data
export const accountLockoutUtils = {
  /**
   * Check if account is currently locked based on API response
   * @param {Object} loginResult - API response object
   * @returns {boolean} - true if account is locked
   */
  isAccountLocked(loginResult) {
    // Account is locked if we have reached the maximum failed attempts
    return loginResult?.failedLoginAttempts >= CONFIG.MAX_LOGIN_ATTEMPTS;
  },
};

export const sessionManager = new SessionManager();
// Backward compatibility - export accountLockoutUtils as accountLockout for existing code
export const accountLockout = accountLockoutUtils;

export const emailValidation = {
  validateClientEmail(email) {
    const generalPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return generalPattern.test(email);
  },

  validateEmbarcEmail(email) {
    const embarcPattern = /^[a-zA-Z0-9._%+-]+@evicore\.com$/;
    return embarcPattern.test(email);
  },

  validateEmail(email) {
    return this.validateClientEmail(email) || this.validateEmbarcEmail(email);
  },

  getUserType(email) {
    if (this.validateClientEmail(email)) {
      return 'client';
    }
    if (this.validateEmbarcEmail(email)) {
      return 'embarc';
    }
    return 'invalid';
  }
};

export const passwordValidation = {
  validatePassword(password) {
    const errors = {};

    if (password.length < 8) {
      errors.length = 'Password must be at least 8 characters long';
    }

    if (!/[A-Z]/.test(password)) {
      errors.uppercase = 'Password must contain at least one uppercase letter';
    }

    if (!/[a-z]/.test(password)) {
      errors.lowercase = 'Password must contain at least one lowercase letter';
    }

    if (!/\d/.test(password)) {
      errors.number = 'Password must contain at least one number';
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.special = 'Password must contain at least one special character';
    }

    return errors;
  },

  getPasswordStrength(password) {
    const errors = this.validatePassword(password);
    const errorCount = Object.keys(errors).length;

    if (password.length === 0) {
      return PASSWORD_STRENGTH.NONE;
    }
    if (errorCount >= 4) {
      return PASSWORD_STRENGTH.WEAK;
    }
    if (errorCount >= 2) {
      return PASSWORD_STRENGTH.MEDIUM;
    }
    if (errorCount === 1) {
      return PASSWORD_STRENGTH.GOOD;
    }
    return PASSWORD_STRENGTH.STRONG;
  },

  checkPasswordExpiry(lastChangeDate) {
    const EXPIRY_DAYS = 45;
    const now = new Date();
    const changeDate = new Date(lastChangeDate);
    const daysSinceChange = Math.floor((now - changeDate) / (1000 * 60 * 60 * 24));
    const daysUntilExpiry = EXPIRY_DAYS - daysSinceChange;

    return {
      daysUntilExpiry,
      isExpired: daysUntilExpiry <= 0,
      needsWarning: daysUntilExpiry <= 10
    };
  }
};

export default sessionManager;
